import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { AppContext, LagoonContext } from '@src/AppContext';
import { event } from '@uhg-abyss/web/tools/event';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { LeftNav } from './LeftNav';
import { OverviewCard } from './OverviewCard';
import { RefineResult } from './RefineResult';
import { SortBy } from './SortBy';
import { Filters } from './Filters';
import { TabletView } from './TabletView/TabletView';

const Wrapper = styled('div', {
  display: 'flex',
});

const Container = styled('div', {
  padding: '40px 0px 40px 20px',
  '@screen >= $xs': {
    paddingRight: '20px',
    width: '100%',
  },
  '@screen >= $xl': {
    paddingRight: '0px',
    width: '70%',
  },
});

const PageHeading = styled('h1', {
  color: 'rgb(0, 38, 119)',
  fontSize: '36px',
  fontWeight: '600',
  lineHeight: '40px',
  margin: '10px 0',
});

const PageContent = styled('div', {
  padding: '30px 0',
});

const SubTitle = styled('h5', {
  minHeight: '24px',
  color: 'rgb(51, 51, 51)',
  fontSize: '15px',
  fontWeight: 400,
  letterSpacing: '0px',
  lineHeight: '24px',

  '@screen >= $xs': {
    display: 'block',
  },

  '@screen >= $xl': {
    display: 'none',
  },
});

export const AllAPIs = ({ appLocation }) => {
  const { apiList = [], filters, updateFilters } = React.useContext(AppContext);
  const { getLocation } = useRouter();
  const location = getLocation();
  const dsUserInfo = useSelector((state) => {
    return state.GET_DS_USER_INFO.data;
  });
  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext?.getTable('feature-toggles');
  const trackitToggle = featureToggles?.getRow('TRACKIT_TOGGLE')?.enabled;
  const dentalToggle = featureToggles?.getRow('DENTALAPI_TOGGLE')?.enabled;
  const superConnectorToggle = featureToggles?.getRow(
    'SUPER_CONNECTOR_TOGGLE'
  )?.enabled;
  const claimsStatusInquiryToggle = featureToggles?.getRow(
    'CLAIMS_STATUS_TOGGLE'
  )?.enabled;
  const benefitCheckToggle = featureToggles?.getRow(
    'BENEFIT_CHECK_TOGGLE'
  )?.enabled;
  const priorAuthReferralToggle = featureToggles?.getRow(
    'PRIOR_AUTH_REFERRAL_TOGGLE'
  )?.enabled;
  const providerAccessToggle = featureToggles?.getRow(
    'PROVIDER_ACCESS_TOGGLE'
  )?.enabled;
  let apiListData = trackitToggle
    ? apiList
    : apiList.filter((api) => {
        return api.key !== 'TrackIt';
      });
  apiListData = dentalToggle
    ? apiListData
    : apiListData.filter((api) => {
        return api.key !== 'Dental';
      });
  apiListData = superConnectorToggle
    ? apiListData
    : apiListData.filter((api) => {
        return api.key !== 'Claim Precheck Inquiry';
      });
  apiListData = superConnectorToggle
    ? apiListData
    : apiListData.filter((api) => {
        return api.key !== 'Member Eligibility Check';
      });
  apiListData = superConnectorToggle
    ? apiListData
    : apiListData.filter((api) => {
        return api.key !== 'Claim Actions';
      });
  apiListData = superConnectorToggle
    ? apiListData
    : apiListData.filter((api) => {
        return api.key !== 'Benefit Check';
      });
  apiListData = superConnectorToggle
    ? apiListData
    : apiListData.filter((api) => {
        return api.key !== 'Prior Auth & Referral Submission';
      });
  apiListData = superConnectorToggle
    ? apiListData
    : apiListData.filter((api) => {
        return api.key !== 'Claims Inquiry';
      });
  apiListData = superConnectorToggle
    ? apiListData
    : apiListData.filter((api) => {
        return api.key !== 'Payments/Remittance';
      });
  apiListData = providerAccessToggle
    ? apiListData
    : apiListData.filter((api) => {
        return api.key !== 'Provider Access';
      });

  useEffect(() => {
    event('ALL_API_PAGE_LOAD', { dsUserInfo, userInfo });
    if (appLocation?.state) {
      const newFilters = filters.map((el) => {
        const checked = el.title === appLocation.state.filter;
        return {
          ...el,
          checked,
        };
      });
      updateFilters(newFilters);
    } else if (!location?.pathname.includes('/all-apis')) {
      const newFilters = filters.map((el) => {
        const checked = false;
        return {
          ...el,
          checked,
        };
      });
      updateFilters(newFilters);
    }
  }, []);

  return (
    <Wrapper>
      <LeftNav>
        <RefineResult />
        <SortBy />
        <Filters />
      </LeftNav>
      <Container>
        <PageHeading>All APIs</PageHeading>
        <TabletView />
        <PageContent>
          <SubTitle>Showing {apiListData.length} Results</SubTitle>
          {apiListData.map((el, idx) => {
            const key = `${el.id}_${idx}`;
            return (
              <OverviewCard
                key={key}
                comingSoon={el.comingSoon || false}
                src={el.src}
                title={el.label}
                label={
                  <React.Fragment>
                    {el.label}
                    {el?.comingSoon ||
                    (el.label === 'Claim Status Inquiry' &&
                      claimsStatusInquiryToggle) ||
                    (el.label === 'Benefit Check' && benefitCheckToggle) ||
                    (el.label === 'Prior Auth and Referral Actions' &&
                      priorAuthReferralToggle) ? (
                      <Badge
                        variant="neutral"
                        outline
                        style={{
                          display: 'inlineBlock',
                          marginLeft: '10px',
                          marginTop: '4px',
                          padding: '1px 5px 0px 4px', // Ensure the Badge is inline
                          // Add spacing between text and Badge
                          fontSize: '14px',
                          fontFamily: 'Enterprise Sans',
                          fontWeight: 'bold !important',
                        }}
                        ariaText="information"
                      >
                        Coming soon
                      </Badge>
                    ) : null}
                  </React.Fragment>
                }
                description={
                  (el.label === 'Claim Status Inquiry' &&
                    claimsStatusInquiryToggle) ||
                  (el.label === 'Benefit Check' && benefitCheckToggle) ||
                  (el.label === 'Prior Auth and Referral Actions' &&
                    priorAuthReferralToggle)
                    ? `Coming Soon: ${el.description}`
                    : el.description
                }
                route={
                  !el.route ||
                  (el.label === 'Claim Status Inquiry' &&
                    claimsStatusInquiryToggle) ||
                  (el.label === 'Benefit Check' && benefitCheckToggle) ||
                  (el.label === 'Prior Auth and Referral Actions' &&
                    priorAuthReferralToggle)
                    ? null
                    : el.route
                }
              />
            );
          })}
        </PageContent>
      </Container>
    </Wrapper>
  );
};

AllAPIs.propTypes = {
  appLocation: PropTypes.shape({
    state: PropTypes.arrayOf(PropTypes.string),
  }),
};

AllAPIs.defaultProps = {
  appLocation: {},
};
